import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { collection, doc, setDoc, getDocs, deleteDoc, updateDoc, query } from 'firebase/firestore';
import { db } from '../../firebase';
import Visibility from '@mui/icons-material/Visibility';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import ArrowBack from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import Alerts from '../../components/Alerts';

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    boxShadow: theme.shadows[6],
  },
}));

const MenuImage = styled('img')(({ theme }) => ({
  width: 50,
  height: 50,
  objectFit: 'cover',
  borderRadius: theme.shape.borderRadius,
  display: 'block',
}));

const DetailImage = styled('img')(({ theme }) => ({
  width: 200,
  height: 200,
  objectFit: 'cover',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  display: 'block',
}));

const ClickableTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const MasterMenu = () => {
  const [view, setView] = useState('viewMenu');
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [ingredients, setIngredients] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [available, setAvailable] = useState('');
  const [staff, setStaff] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterCategory, setFilterCategory] = useState('All');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'menu'));
        const menuData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMenuItems(menuData);
        applyFilters(menuData, searchQuery, filterType, filterCategory);
      } catch (err) {
        console.error('Error fetching menu items:', err);
        setError('Failed to load menu data. Check Firestore permissions or data.');
      }
    };
    fetchMenuItems();
  }, []);

  const applyFilters = (items, query, type, category) => {
    let filtered = items;
    if (query) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(query.toLowerCase())
      );
    }
    if (type !== 'All') {
      filtered = filtered.filter((item) => item.type === type);
    }
    if (category !== 'All') {
      filtered = filtered.filter((item) => item.category === category);
    }
    setFilteredItems(filtered);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(menuItems, query, filterType, filterCategory);
  };

  // FIXED
  const handleFilterTypeChange = (e) => {
    const type = e.target.value;
    setFilterType(type);
    applyFilters(menuItems, searchQuery, type, filterCategory);
  };

  const handleFilterCategoryChange = (e) => {
    const category = e.target.value;
    setFilterCategory(category);
    applyFilters(menuItems, searchQuery, filterType, category);
  };


  const handleAddItem = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setLoading(true);

    const errors = {};
    if (!name || /[\\/]/.test(name)) {
      errors.name = 'Item name cannot be empty or contain "/" or "\\".';
    }
    if (!category) {
      errors.category = 'Please select a category.';
    }
    if (!type) {
      errors.type = 'Please select a type (Veg or Non-Veg).';
    }
    if (!price || isNaN(price) || price <= 0) {
      errors.price = 'Price must be a positive number.';
    }
    if (!description) {
      errors.description = 'Description cannot be empty.';
    }
    if (!ingredients) {
      errors.ingredients = 'Ingredients cannot be empty.';
    }
    if (!imageUrl || !/^https?:\/\/.+\..+/.test(imageUrl)) {
      errors.imageUrl = 'Please enter a valid URL (e.g., https://example.com/image.jpg).';
    }
    if (available === '') {
      errors.available = 'Please specify if the item is available.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const itemDocRef = doc(db, 'menu', name);
      await setDoc(itemDocRef, {
        name,
        category,
        type,
        price: Number(price),
        description,
        ingredients,
        imageUrl,
        available: available === 'true',
        staff: staff || '',
        createdAt: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Menu item added successfully!',
        severity: 'success',
      });
      resetForm();
      setOpenAddDialog(false);
      const querySnapshot = await getDocs(collection(db, 'menu'));
      const updatedItems = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setMenuItems(updatedItems);
      applyFilters(updatedItems, searchQuery, filterType, filterCategory);
    } catch (err) {
      console.error('Error adding menu item:', err);
      setFormErrors({ general: err.message || 'Failed to add menu item.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setLoading(true);

    const errors = {};
    if (!name || /[\\/]/.test(name)) {
      errors.name = 'Item name cannot be empty or contain "/" or "\\".';
    }
    if (!category) {
      errors.category = 'Please select a category.';
    }
    if (!type) {
      errors.type = 'Please select a type (Veg or Non-Veg).';
    }
    if (!price || isNaN(price) || price <= 0) {
      errors.price = 'Price must be a positive number.';
    }
    if (!description) {
      errors.description = 'Description cannot be empty.';
    }
    if (!ingredients) {
      errors.ingredients = 'Ingredients cannot be empty.';
    }
    if (!imageUrl || !/^https?:\/\/.+\..+/.test(imageUrl)) {
      errors.imageUrl = 'Please enter a valid URL (e.g., https://example.com/image.jpg).';
    }
    if (available === '') {
      errors.available = 'Please specify if the item is available.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const itemDocRef = doc(db, 'menu', selectedItem.id);
      await updateDoc(itemDocRef, {
        name,
        category,
        type,
        price: Number(price),
        description,
        ingredients,
        imageUrl,
        available: available === 'true',
        staff: staff || '',
        createdAt: selectedItem.createdAt,
        updatedAt: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: 'Menu item updated successfully!',
        severity: 'success',
      });
      resetForm();
      setOpenEditDialog(false);
      setSelectedItem(null);
      const querySnapshot = await getDocs(collection(db, 'menu'));
      const updatedItems = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setMenuItems(updatedItems);
      applyFilters(updatedItems, searchQuery, filterType, filterCategory);
    } catch (err) {
      console.error('Error updating menu item:', err);
      setFormErrors({ general: err.message || 'Failed to update menu item.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async () => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, 'menu', itemToDelete.id));
      setSnackbar({
        open: true,
        message: 'Menu item deleted successfully!',
        severity: 'success',
      });
      const querySnapshot = await getDocs(collection(db, 'menu'));
      const updatedItems = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setMenuItems(updatedItems);
      applyFilters(updatedItems, searchQuery, filterType, filterCategory);
      setOpenDeleteDialog(false);
      setItemToDelete(null);
      if (view === 'itemDetails') {
        setView('viewMenu');
        setSelectedItem(null);
      }
    } catch (err) {
      console.error('Error deleting menu item:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete menu item.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setCategory('');
    setType('');
    setPrice('');
    setDescription('');
    setIngredients('');
    setImageUrl('');
    setAvailable('');
    setStaff('');
    setFormErrors({});
  };

  const handleInputChange = (setter, field) => (e) => {
    setter(e.target.value);
    setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const openEditForm = (item) => {
    setSelectedItem(item);
    setName(item.name);
    setCategory(item.category);
    setType(item.type);
    setPrice(item.price);
    setDescription(item.description);
    setIngredients(item.ingredients);
    setImageUrl(item.imageUrl);
    setAvailable(item.available ? 'true' : 'false');
    setStaff(item.staff || '');
    setOpenEditDialog(true);
  };

  const placeholderImage = 'https://via.placeholder.com/150?text=No+Image';

  const renderContent = () => {
    if (view === 'viewMenu') {
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Menu Items
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                resetForm();
                setOpenAddDialog(true);
              }}
              sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
            >
              Add Item
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                onChange={handleFilterTypeChange}
                label="Type"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="veg">Veg</MenuItem>
                <MenuItem value="nonveg">Non-Veg</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                onChange={handleFilterCategoryChange}
                label="Category"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Starters">Starters</MenuItem>
                <MenuItem value="Main Course">Main Course</MenuItem>
                <MenuItem value="Desserts">Desserts</MenuItem>
                <MenuItem value="Beverages">Beverages</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Search by Name"
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ flexGrow: 1, maxWidth: 300, borderRadius: '8px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {filteredItems.length > 0 ? (
            <StyledCard>
              <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.default' }}>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Image</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Category</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Price</TableCell>
                      <TableCell sx={{ color: 'text.primary', fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredItems.map((item) => (
                      <ClickableTableRow
                        key={item.id}
                        onClick={() => {
                          setSelectedItem(item);
                          setView('itemDetails');
                        }}
                      >
                        <TableCell>
                          <MenuImage
                            src={item.imageUrl}
                            alt={item.name}
                            onError={(e) => (e.target.src = placeholderImage)}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{item.name}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{item.category}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>{item.type}</TableCell>
                        <TableCell sx={{ color: 'text.primary' }}>₹{item.price}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              onClick={() => openEditForm(item)}
                              sx={{ color: 'primary.main' }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setItemToDelete(item);
                                setOpenDeleteDialog(true);
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </ClickableTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledCard>
          ) : (
            <Typography color="text.secondary">
              No menu items available. Add an item or adjust filters.
            </Typography>
          )}
        </Box>
      );
    }

    if (view === 'itemDetails' && selectedItem) {
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton
              onClick={() => setView('viewMenu')}
              sx={{ mr: 2, color: 'text.primary' }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Item Details: {selectedItem.name}
            </Typography>
          </Box>
          <StyledCard>
            <CardContent>
              <DetailImage
                src={selectedItem.imageUrl}
                alt={selectedItem.name}
                onError={(e) => (e.target.src = placeholderImage)}
              />
              <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2, color: 'text.primary' }}>
                Item Information
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Name:</strong> {selectedItem.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Category:</strong> {selectedItem.category}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Type:</strong> {selectedItem.type}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Price:</strong> ₹{selectedItem.price}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Description:</strong> {selectedItem.description}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Ingredients:</strong> {selectedItem.ingredients}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Image URL:</strong> <a href={selectedItem.imageUrl} target="_blank" rel="noopener noreferrer">{selectedItem.imageUrl}</a>
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Available:</strong> {selectedItem.available ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Staff:</strong> {selectedItem.staff || 'None'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Created At:</strong> {new Date(selectedItem.createdAt).toLocaleString()}
              </Typography>
              {selectedItem.updatedAt && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Updated At:</strong> {new Date(selectedItem.updatedAt).toLocaleString()}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => openEditForm(selectedItem)}
                  sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
                >
                  Edit Item
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => {
                    setItemToDelete(selectedItem);
                    setOpenDeleteDialog(true);
                  }}
                  sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
                >
                  Delete Item
                </Button>
              </Box>
            </CardContent>
          </StyledCard>
        </Box>
      );
    }
  };

  const renderFormDialog = (isEdit = false) => (
    <Dialog
      open={isEdit ? openEditDialog : openAddDialog}
      onClose={() => {
        resetForm();
        if (isEdit) setOpenEditDialog(false);
        else setOpenAddDialog(false);
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', bgcolor: 'background.paper' } }}
    >
      <DialogTitle>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          {isEdit ? 'Edit Menu Item' : 'Add Menu Item'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {formErrors.general && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {formErrors.general}
          </Alert>
        )}
        <form onSubmit={isEdit ? handleEditItem : handleAddItem}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              fullWidth
              label="Item Name"
              variant="outlined"
              value={name}
              onChange={handleInputChange(setName, 'name')}
              required
              error={!!formErrors.name}
              helperText={formErrors.name || ''}
              sx={{ borderRadius: '8px' }}
            />
            <FormControl fullWidth required error={!!formErrors.category}>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                onChange={handleInputChange(setCategory, 'category')}
                label="Category"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="Starters">Starters</MenuItem>
                <MenuItem value="Main Course">Main Course</MenuItem>
                <MenuItem value="Desserts">Desserts</MenuItem>
                <MenuItem value="Beverages">Beverages</MenuItem>
              </Select>
              {formErrors.category && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {formErrors.category}
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth required error={!!formErrors.type}>
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                onChange={handleInputChange(setType, 'type')}
                label="Type"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="veg">Veg</MenuItem>
                <MenuItem value="nonveg">Non-Veg</MenuItem>
              </Select>
              {formErrors.type && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {formErrors.type}
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label="Price (₹)"
              variant="outlined"
              value={price}
              onChange={handleInputChange(setPrice, 'price')}
              required
              type="number"
              error={!!formErrors.price}
              helperText={formErrors.price || 'Enter price in INR'}
              sx={{ borderRadius: '8px' }}
            />
            <TextField
              fullWidth
              label="Description"
              variant="outlined"
              value={description}
              onChange={handleInputChange(setDescription, 'description')}
              required
              multiline
              rows={3}
              error={!!formErrors.description}
              helperText={formErrors.description || ''}
              sx={{ borderRadius: '8px' }}
            />
            <TextField
              fullWidth
              label="Ingredients"
              variant="outlined"
              value={ingredients}
              onChange={handleInputChange(setIngredients, 'ingredients')}
              required
              error={!!formErrors.ingredients}
              helperText={formErrors.ingredients || 'Comma-separated list of ingredients'}
              sx={{ borderRadius: '8px' }}
            />
            <TextField
              fullWidth
              label="Image URL"
              variant="outlined"
              value={imageUrl}
              onChange={handleInputChange(setImageUrl, 'imageUrl')}
              required
              type="url"
              error={!!formErrors.imageUrl}
              helperText={formErrors.imageUrl || 'Enter a valid image URL (e.g., https://example.com/image.jpg)'}
              sx={{ borderRadius: '8px' }}
            />
            <FormControl fullWidth required error={!!formErrors.available}>
              <InputLabel>Available</InputLabel>
              <Select
                value={available}
                onChange={handleInputChange(setAvailable, 'available')}
                label="Available"
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
              {formErrors.available && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {formErrors.available}
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label="Staff (Optional)"
              variant="outlined"
              value={staff}
              onChange={handleInputChange(setStaff, 'staff')}
              helperText="Enter staff name or leave empty"
              sx={{ borderRadius: '8px' }}
            />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  resetForm();
                  if (isEdit) setOpenEditDialog(false);
                  else setOpenAddDialog(false);
                }}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  px: 4,
                  py: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={loading}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  px: 4,
                  py: 1,
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.02)' },
                }}
              >
                {isEdit ? 'Update Item' : 'Add Item'}
              </LoadingButton>
            </Box>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 3 }}>
          Menu Management
        </Typography>
        {renderContent()}
        {renderFormDialog(false)}
        {renderFormDialog(true)}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px', bgcolor: 'background.paper' } }}
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              Confirm Delete
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenDeleteDialog(false)}
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleDeleteItem}
              loading={loading}
              color="error"
              variant="contained"
              sx={{ borderRadius: '8px', textTransform: 'none' }}
            >
              Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
        <Alerts
          snackbar={snackbar}
          handleClose={handleCloseSnackbar}
          darkMode={false}
        />
      </Box>
    </Box>
  );
};

export default MasterMenu;